// Client-side bucketing for the homepage hero experiment, matching API's
// FeatureSwitch.chooseVariantForId so the arm we show equals the logged variant.

export const HOMEPAGE_EXPERIMENT_SALT = "0.9039735261451767";

// Ramp lever: grow the upper bounds only (fixed lower anchors) so arms expand
// monotonically — visitors move off→enrolled, never test↔control. Bump the
// version too, since the decision is sticky.
export const HP_TEST_MAX = 0.25; //    test    = [0, HP_TEST_MAX)
export const HP_CONTROL_MIN = 0.5; //  control = [HP_CONTROL_MIN, HP_CONTROL_MAX)
export const HP_CONTROL_MAX = 0.75;

// Cookie version: bump on every ramp so cached decisions re-bucket once against
// the new ranges; the head script also clears stale-version cookies.
export const HOMEPAGE_EXPERIMENT_VERSION = "v1";
export const HOMEPAGE_VARIANT_COOKIE_BASE = "cd_hp_exp_2026";
export const HOMEPAGE_VARIANT_COOKIE = `${HOMEPAGE_VARIANT_COOKIE_BASE}_${HOMEPAGE_EXPERIMENT_VERSION}`;

export type HpVariant = "test" | "control" | "off";

// Synchronous SHA-1 → lowercase hex (inlined into the head script, so no comments inside).
export function sha1Hex(str: string): string {
  const utf8 = unescape(encodeURIComponent(str));
  const bytes: number[] = [];
  for (let i = 0; i < utf8.length; i++) bytes.push(utf8.charCodeAt(i) & 0xff);

  const ml = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  for (let i = 7; i >= 0; i--) bytes.push((ml / Math.pow(2, i * 8)) & 0xff);

  let h0 = 0x67452301,
    h1 = 0xefcdab89,
    h2 = 0x98badcfe,
    h3 = 0x10325476,
    h4 = 0xc3d2e1f0;

  const rotl = (n: number, s: number) => ((n << s) | (n >>> (32 - s))) >>> 0;

  for (let chunk = 0; chunk < bytes.length; chunk += 64) {
    const w = new Array(80);
    for (let i = 0; i < 16; i++) {
      const j = chunk + i * 4;
      w[i] = ((bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3]) >>> 0;
    }
    for (let i = 16; i < 80; i++) w[i] = rotl(w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1);

    let a = h0,
      b = h1,
      c = h2,
      d = h3,
      e = h4;
    for (let i = 0; i < 80; i++) {
      let f: number, k: number;
      if (i < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (i < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const t = (rotl(a, 5) + f + e + k + w[i]) >>> 0;
      e = d;
      d = c;
      c = rotl(b, 30);
      b = a;
      a = t;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  const toHex = (n: number) => ("00000000" + (n >>> 0).toString(16)).slice(-8);
  return toHex(h0) + toHex(h1) + toHex(h2) + toHex(h3) + toHex(h4);
}

// Mirrors API's chooseVariantForId. Ranges are args (not module constants) so the
// function is self-contained when inlined into the head script via .toString().
export function pickHomepageVariant(
  id: string,
  salt: string,
  testMax: number,
  controlMin: number,
  controlMax: number,
): HpVariant {
  if (!id || id === "redacted") return "off";
  const hex = sha1Hex(`${id}:${salt}`);
  const bucket = Number(BigInt(`0x${hex}`) % BigInt(10000)) / 10000;
  if (bucket >= 0 && bucket < testMax) return "test";
  if (bucket >= controlMin && bucket < controlMax) return "control";
  return "off";
}

// Affirmative performance-cookie consent (OneTrust C0002:1) — the enrollment gate,
// since that's when the log client keeps a stable dojo_log_session_id.
export function hasPerformanceConsent(): boolean {
  if (typeof document === "undefined") return false;
  const m = document.cookie.match(/(?:^|; )OptanonConsent=([^;]+)/);
  if (!m) return false;
  const groups = new URLSearchParams(decodeURIComponent(m[1])).get("groups");
  return !!groups && groups.includes("C0002:1");
}

// Pre-paint <head> script (runs on `/` and `/{locale}/`): reuses the sticky versioned
// cookie if present, else buckets only when consent is already affirmative and a
// session id exists; marks <html data-hp-variant="test"> so CSS shows the right hero
// with no flash. Composed from the tested functions above via .toString() (no drift).
export function buildHomepageHeadScript(): string {
  return (
    "(function(){try{" +
    "var p=location.pathname;if(!/^\\/(?:[a-z]{2,3}-[a-z]{2}\\/)?(?:index\\.html)?$/.test(p)){return;}" +
    sha1Hex.toString() +
    pickHomepageVariant.toString() +
    hasPerformanceConsent.toString() +
    `var COOKIE=${JSON.stringify(HOMEPAGE_VARIANT_COOKIE)},BASE=${JSON.stringify(HOMEPAGE_VARIANT_COOKIE_BASE)};` +
    // Read the current versioned decision; delete stale-version cookies in the same pass.
    "var ck=document.cookie.split('; '),v=null;" +
    "for(var i=0;i<ck.length;i++){var nm=ck[i].split('=')[0];" +
    "if(nm===COOKIE){v=ck[i].slice(COOKIE.length+1);}" +
    "else if(nm&&nm.indexOf(BASE)===0){document.cookie=nm+'=; Max-Age=0; Path=/';}}" +
    // Enroll at paint only when consent is affirmative and a session id already exists.
    "if(v!=='test'&&v!=='control'&&v!=='off'&&hasPerformanceConsent()){" +
    "var s=document.cookie.match(/(?:^|; )dojo_log_session_id=([^;]+)/);" +
    "var id=s?decodeURIComponent(s[1]):'';" +
    "if(id&&id!=='redacted'){" +
    `v=pickHomepageVariant(id,${JSON.stringify(
      HOMEPAGE_EXPERIMENT_SALT,
    )},${HP_TEST_MAX},${HP_CONTROL_MIN},${HP_CONTROL_MAX});` +
    "document.cookie=COOKIE+'='+v+'; Max-Age=2592000; Path=/; Secure; SameSite=Lax';" +
    "}}" +
    "if(v==='test'){document.documentElement.setAttribute('data-hp-variant','test');}" +
    "}catch(e){}})();"
  );
}
